<script setup lang="ts">
import VeeCheckbox from '@/components/forms/Checkbox/VeeCheckbox.vue';
import VeeNumberField from '@/components/forms/NumberField/VeeNumberField.vue';
import VeePasswordField from '@/components/forms/PasswordField/VeePasswordField.vue';
import TextField from '@/components/forms/TextField/TextField.vue';
import VeeTextField from '@/components/forms/TextField/VeeTextField.vue';
import { error, success } from '@/components/icons/icons';
import Loader from '@/components/Loader/Loader.vue';
import Notification from '@/components/notifications/Notification.vue';
import type { NotificationMessage } from '@/components/notifications/types.ts';
import { useTranslation } from 'i18next-vue';
import { computed } from 'vue';

export interface DatabaseSettingsProps {
  // Are we in the installation or settings?
  isFirstTimeInstallation?: boolean;
  // Database type state (not in form schema)
  isCustomDatabaseUsed?: boolean;
  // Computed connection strings for display
  defaultConnectionString?: string;
  blobStoringConnectionString?: string;
  // UI state
  isProcessing?: boolean;
  isRebooting?: boolean;
  isWaitingForReboot?: boolean;
  showSuccessMessage?: boolean;
  showErrorMessage?: boolean;
  showSelectionValidation?: boolean;
  // Password visibility state
  isDefaultPasswordVisible?: boolean;
  isBlobStoringPasswordVisible?: boolean;
  databaseType: 'integrated' | 'custom';
}

const {
  isFirstTimeInstallation = false,
  isCustomDatabaseUsed,
  defaultConnectionString = '',
  blobStoringConnectionString = '',
  isProcessing = false,
  isRebooting = false,
  isWaitingForReboot = false,
  showSuccessMessage = false,
  showErrorMessage = false,
  showSelectionValidation = false,
  isDefaultPasswordVisible = false,
  isBlobStoringPasswordVisible = false,
  databaseType,
} = defineProps<DatabaseSettingsProps>();

// Define emits
const emit = defineEmits<{
  'update:databaseType': [type: 'integrated' | 'custom'];
  'update:passwordVisibility': [field: 'default' | 'blob', visible: boolean];
}>();

const { t } = useTranslation();

function handleDatabaseTypeChange(value: string | null) {
  if (value && (value === 'integrated' || value === 'custom')) {
    // Notify parent component - the parent will update the form
    emit('update:databaseType', value);
  }
}

function handleDefaultPasswordVisibilityChange(visible: boolean) {
  emit('update:passwordVisibility', 'default', visible);
}

function handleBlobPasswordVisibilityChange(visible: boolean) {
  emit('update:passwordVisibility', 'blob', visible);
}

const integratedDatabaseWarning = computed<NotificationMessage>(() => ({
  type: 'warning',
  message: t('DatabaseProductionWarningMsg'),
  closable: false,
}));
const customDatabaseWarning = computed<NotificationMessage>(() => ({
  type: 'warning',
  message: t('AlreadyExistingDatabaseWillBeMigratedMsg'),
  closable: false,
}));
const databaseSelectionIsUnchangedValidation = computed<NotificationMessage | undefined>(() =>
  showSelectionValidation
    ? {
        type: 'error',
        message: t('TheDatabaseSelectionHasNotBeenChanged'),
        icon: false,
        closable: false,
      }
    : undefined,
);

const infoText = computed(() => {
  if (databaseType === 'custom') {
    // In installer mode: show CreateACustomDatabaseMsg
    // In settings mode: show NewDatabaseWillBeCreatedMsg
    return isFirstTimeInstallation
      ? t('CreateACustomDatabaseMsg')
      : t('NewDatabaseWillBeCreatedMsg');
  } else {
    return t('IntegratedDatabaseWillBeUsedMsg');
  }
});

const infoNotification = computed(() => {
  if (databaseType === 'custom') {
    return customDatabaseWarning.value;
  } else {
    return undefined;
  }
});

const progressText = computed(() => {
  if (isWaitingForReboot) {
    return t('TheServerIsWaitingForAReboot');
  }

  if (isRebooting) {
    return t('TheServerIsRebooting');
  }

  if (isProcessing && !isRebooting) {
    return t('CanTakeUptoXMinutes', { durationInMinutes: 2 });
  }

  return '';
});
</script>

<template>
  <Loader v-if="progressText" type="overlay" :text="progressText" />

  <h4 class="mb-4">
    {{ $t('DatabaseConfiguration') }}
  </h4>

  <template v-if="!isCustomDatabaseUsed">
    <p class="text-pre-line">
      {{ $t('DatabaseConfigurationMsg') }}
    </p>
    <Notification class="w-lg-50 my-4" :value="integratedDatabaseWarning" />
  </template>

  <v-radio-group
    :inline="true"
    class="d-flex flex-column my-4"
    :modelValue="databaseType"
    @update:modelValue="handleDatabaseTypeChange"
  >
    <v-radio :label="$t('IntegratedDatabase')" value="integrated" :disabled="isProcessing" />
    <v-radio :label="$t('CustomDatabase')" value="custom" :disabled="isProcessing" />
  </v-radio-group>
  <Notification class="my-4 fit-content" :value="databaseSelectionIsUnchangedValidation" />

  <p class="text-pre-line mb-4">
    {{ infoText }}
  </p>
  <Notification class="w-lg-50 my-4" :value="infoNotification" />
  <VeeCheckbox
    v-if="!isFirstTimeInstallation"
    v-show="databaseType === 'custom'"
    name="backupConfirmed"
    :label="$t('IUnderstandAndHaveCreatedABackup')"
  />

  <h4 class="mb-4 mt-8">
    {{ databaseType === 'custom' ? $t('CustomDatabase') : $t('IntegratedDatabase') }}
  </h4>

  <TextField
    :modelValue="defaultConnectionString"
    :mode="'details'"
    :label="$t('ApplicationConnectionString')"
    :appendInnerIcon="isDefaultPasswordVisible ? 'mdi-eye-off' : 'mdi-eye'"
    @click:appendInner="handleDefaultPasswordVisibilityChange(!isDefaultPasswordVisible)"
  />
  <TextField
    :modelValue="blobStoringConnectionString"
    class="mt-6"
    :mode="'details'"
    :label="$t('BlobConnectionString')"
    :appendInnerIcon="isBlobStoringPasswordVisible ? 'mdi-eye-off' : 'mdi-eye'"
    @click:appendInner="handleBlobPasswordVisibilityChange(!isBlobStoringPasswordVisible)"
  />

  <v-row v-show="databaseType === 'custom'" class="w-100 mt-6">
    <v-col>
      <v-row>
        <v-col class="pb-0">
          <h5>
            {{ $t('Application') }}
          </h5>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="pt-0">
          <VeeTextField
            name="defaultConnectionString.server"
            :placeholder="$t('Required')"
            :disabled="isProcessing"
            :label="$t('Server')"
          />
        </v-col>
        <v-col class="pt-0">
          <VeeNumberField
            name="defaultConnectionString.port"
            :disabled="isProcessing"
            :label="$t('Port')"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <VeeTextField
            name="defaultConnectionString.database"
            :placeholder="$t('Required')"
            :disabled="isProcessing"
            :label="$t('Database')"
          />
          <VeeTextField
            name="defaultConnectionString.user"
            class="gutterTop"
            :placeholder="$t('Required')"
            :disabled="isProcessing"
            :label="$t('UserId')"
          />
          <VeePasswordField
            :show="isDefaultPasswordVisible"
            name="defaultConnectionString.password"
            class="gutterTop"
            :placeholder="$t('Required')"
            :disabled="isProcessing"
            :label="$t('Password')"
            @update:show="handleDefaultPasswordVisibilityChange"
          />
          <VeeTextField
            name="defaultConnectionString.additionalParameters"
            class="gutterTop"
            :disabled="isProcessing"
            :label="$t('AdditionalParameters')"
            :infoMessage="$t('AdditionalParametersMsg')"
          />
        </v-col>
      </v-row>
    </v-col>
    <v-col>
      <v-row>
        <v-col class="pb-0">
          <h5>
            {{ $t('Blob') }}
          </h5>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="pt-0">
          <VeeTextField
            name="blobStoringConnectionString.server"
            :placeholder="$t('Required')"
            :disabled="isProcessing"
            :label="$t('Server')"
          />
        </v-col>
        <v-col class="pt-0">
          <VeeNumberField
            name="blobStoringConnectionString.port"
            :disabled="isProcessing"
            :label="$t('Port')"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <VeeTextField
            name="blobStoringConnectionString.database"
            :placeholder="$t('Required')"
            :disabled="isProcessing"
            :label="$t('Database')"
          />
          <VeeTextField
            name="blobStoringConnectionString.user"
            class="gutterTop"
            :placeholder="$t('Required')"
            :disabled="isProcessing"
            :label="$t('UserId')"
          />
          <VeePasswordField
            :show="isBlobStoringPasswordVisible"
            name="blobStoringConnectionString.password"
            class="gutterTop"
            :placeholder="$t('Required')"
            :disabled="isProcessing"
            :label="$t('Password')"
            @update:show="handleBlobPasswordVisibilityChange"
          />
          <VeeTextField
            name="blobStoringConnectionString.additionalParameters"
            class="gutterTop"
            :disabled="isProcessing"
            :label="$t('AdditionalParameters')"
            :infoMessage="$t('AdditionalParametersMsg')"
          />
        </v-col>
      </v-row>
    </v-col>
  </v-row>

  <div class="mt-6">
    <p v-if="isWaitingForReboot" class="font-weight-semibold">
      {{ $t('TheServerIsWaitingForAReboot') }}
    </p>
    <p v-else-if="isRebooting" class="font-weight-semibold">
      {{ $t('TheServerIsRebooting') }}
    </p>
    <p v-else-if="isProcessing && !isRebooting" class="font-weight-semibold">
      {{ $t('CanTakeUptoXMinutes', { durationInMinutes: 2 }) }}
    </p>
  </div>

  <v-alert v-if="showSuccessMessage" class="mt-8 text-center" type="success" :icon="false">
    <template #text>
      <v-icon class="mr-2" :icon="success"></v-icon>
      <span class="font-weight-semibold">{{ $t('DatabaseConnected') }}</span>
    </template>
  </v-alert>
  <v-alert v-if="showErrorMessage" class="mt-8 text-center" type="error" :icon="false">
    <template #text>
      <v-icon class="mr-2" :icon="error"></v-icon>
      <span class="font-weight-semibold">{{ $t('DatabaseErrorMsg') }}</span>
    </template>
  </v-alert>
</template>
